/**
 * How long the live pass has been going, and how long one like it usually takes
 * here.
 *
 * A reasoning pass runs for most of a minute — 45 to 120 s in the study, a
 * source check about as long — and the only figure a pass printed was its
 * seconds once it had landed. Through the wait itself nothing on screen said
 * whether this one was slow, or whether they all take this long. Two figures
 * answer that, and both are the page's alone:
 *
 *  - the elapsed clock, from the op's own `startedAt` — the figure the pass's
 *    meta keeps once it lands, so what ticked is what stays;
 *  - "usually ~50s here", from this browser's own finished passes of the same
 *    kind on the same model.
 *
 * Neither is a promise or a deadline, and nothing but the page reads either: no
 * stop condition, no run policy, no prompt (invariant 6). A pass that runs past
 * its usual figure changes nothing on screen — the one line that speaks up about
 * a slow stream is the quiet watch's, and it is about silence, not about time
 * (`QUIET_MS` in `core/failure.ts`, D218, D244).
 *
 * WHERE THE PASSES COME FROM. A small rolling record in this browser's storage
 * (`PASS_TIMES_KEY`), not the stored explorations, although every op already
 * carries its start and finish. Three reasons. "Here" means this browser's
 * connection, this key's rate tier and this model, and an exploration opened
 * from a share link or a file carries passes timed on somebody else's machine,
 * under the same model stamp — nothing on an op says where it ran. Reading them
 * would mean unpacking every stored document at a pass's start (`core/pack.ts`),
 * for one number. And forgetting an exploration would take its seconds with it,
 * which is a different promise from the one Forget makes. The record holds only
 * a verb, a model id and durations — no claim, no label — and a lost or refused
 * slot costs exactly one line, which then does not show.
 *
 * A narration's seconds are not here and not on the clock. It is its own op
 * (`narration`, run by `ui/narrator.ts` AFTER the pass's `endOp`, off the lock),
 * so the pass's clock has stopped before it begins, and its record is skipped
 * below. Its wait is not an empty one: its words arrive in its own turn, which
 * has its own Stop and its own quiet line (D244, D257).
 */

import { OP_LABEL, type ModelStamp, type Op, type OpKind, type Session } from './types'
import { EXECUTABLE_LABEL, isExecutableTest } from './narrationReceipt'
import { watchLandings, type OpsStore } from './landings'

/**
 * How many finished passes of one kind on one model before "usually" is said.
 *
 * Five, because five is the fewest passes whose range brackets the typical pass
 * more than nine times in ten, with nothing assumed about how the times are
 * spread: the true median lies between the fastest and the slowest of n
 * independent passes with probability 1 − 2·(½)ⁿ — 75% at three, 87.5% at four,
 * 93.75% at five. Below that, "usually" would be a guess at a spread the page
 * has barely seen.
 */
export const USUAL_AFTER = 5

/**
 * How many are kept per kind and model, newest last. Enough that one odd pass —
 * a retry wait, a long answer — moves the median by at most one place; few
 * enough that a change of route, rate tier or model version is the typical
 * figure within a few sittings rather than a few months.
 */
export const USUAL_WINDOW = 15

/**
 * How wide the middle half of those passes may spread, as a share of their
 * median, for "usually" to be said at all. A middle half from 30 s to 150 s has
 * no usual length, and "~60s" over it would be a figure the record does not
 * stand behind; one from 40 s to 70 s does.
 */
export const USUAL_SPREAD = 1

/** The rolling record's one slot. Beside the preferences, never inside a
 *  document: it is about this browser, not about any exploration. */
export const PASS_TIMES_KEY = 'nv.passTimes'

/** Durations in milliseconds per kind and model, newest last. */
export type PassTimes = Record<string, number[]>

/**
 * What a pass's seconds are pooled under: its op kind, except for a conjecture's
 * executable test. That is filed as a `research` op (D283), and pooled with the
 * Lab's analyses it gave each the other's "usually" — a test-writing pass at high
 * effort and a structured comparison are not one length of pass (D289).
 */
export type PassTimeKind = OpKind | 'executable'

/** The kind a pass's time is recorded and looked up under — `isExecutableTest`,
 *  the one predicate every surface names that pass by. */
export const passTimeKind = (session: Pick<Session, 'research'>, op: Op): PassTimeKind =>
  isExecutableTest(session, op) ? 'executable' : op.kind

/** One row per verb and per model the op names — its provider as well as its id,
 *  so a custom endpoint's model named like a hosted one is not pooled with it. */
export const timeKey = (kind: PassTimeKind, model: ModelStamp): string =>
  `${kind}|${model.provider}:${model.id}`

/**
 * How long a pass took, if its time says how long one takes: finished, run on a
 * model (the worked example's unfurl ran on none and is paced by the page), not
 * a narration, and neither failed nor declined — a pass stopped at 3 s or turned
 * away before a byte came back is not a short pass.
 */
export function timedPass(op: Op): number | null {
  if (op.finishedAt === undefined || !op.model || op.narration || op.error || op.refused) {
    return null
  }
  const ms = op.finishedAt - op.startedAt
  return Number.isFinite(ms) && ms > 0 ? ms : null
}

/** The record with this pass's time added, the oldest beyond the window dropped. */
export function withPassTime(times: PassTimes, op: Op, kind: PassTimeKind = op.kind): PassTimes {
  const ms = timedPass(op)
  if (ms === null || !op.model) return times
  const k = timeKey(kind, op.model)
  return { ...times, [k]: [...(times[k] ?? []), ms].slice(-USUAL_WINDOW) }
}

/** The record as stored, whatever is in the slot: anything that is not a list of
 *  positive durations is dropped rather than trusted. */
export function readPassTimes(): PassTimes {
  let raw: unknown
  try {
    raw = JSON.parse(localStorage.getItem(PASS_TIMES_KEY) ?? '{}')
  } catch {
    return {}
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: PassTimes = {}
  for (const [k, v] of Object.entries(raw)) {
    if (!Array.isArray(v)) continue
    const ms = v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x) && x > 0)
    if (ms.length) out[k] = ms.slice(-USUAL_WINDOW)
  }
  return out
}

/** Add one finished pass. Read fresh each time, so another tab's passes are kept. */
export function recordPassTime(op: Op, kind: PassTimeKind = op.kind): void {
  if (timedPass(op) === null) return
  try {
    localStorage.setItem(PASS_TIMES_KEY, JSON.stringify(withPassTime(readPassTimes(), op, kind)))
  } catch {
    /* private mode, or a full origin: the line simply does not show */
  }
}

/** What "usually" says: the rounded median, and how many passes it came from. */
export interface Usual {
  secs: number
  passes: number
}

/** A quantile by linear interpolation between the two nearest ranks. */
const quantile = (sorted: readonly number[], p: number): number => {
  const i = p * (sorted.length - 1)
  const lo = Math.floor(i)
  const hi = Math.ceil(i)
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (i - lo)
}

/**
 * The figure, rounded to about a tenth of itself: to 5 s under a minute, to 10 s
 * from there. The median of a handful of passes that differ by tens of seconds
 * is not known to the second, and a figure printed to the second would say it
 * was. Never below 5 s — "~0s" is not a length.
 */
export const roundUsual = (secs: number): number =>
  secs < 60 ? Math.max(5, Math.round(secs / 5) * 5) : Math.round(secs / 10) * 10

/** "Usually", from durations in ms — or null until there are `USUAL_AFTER` of
 *  them and while their middle half spreads wider than `USUAL_SPREAD` allows. */
export function usualOf(samples: readonly number[]): Usual | null {
  if (samples.length < USUAL_AFTER) return null
  const sorted = [...samples].sort((a, b) => a - b)
  const median = quantile(sorted, 0.5)
  const spread = quantile(sorted, 0.75) - quantile(sorted, 0.25)
  if (spread > median * USUAL_SPREAD) return null
  return { secs: roundUsual(median / 1000), passes: samples.length }
}

/** "Usually" for one kind of pass on one model, from this browser's record. */
export function usualFor(kind: PassTimeKind, model: ModelStamp | undefined): Usual | null {
  if (!model) return null
  return usualOf(readPassTimes()[timeKey(kind, model)] ?? [])
}

/** The clock's face: whole seconds, counted up, in the form the pass's meta keeps
 *  once it lands ("52s"), so the figure that ticked is the figure that stays. */
export const elapsedWords = (ms: number): string => `${Math.floor(Math.max(0, ms) / 1000)}s`

/** The line as printed. "Usually" and a tilde: never "about to", never "left". */
export const usualWords = (u: Usual): string => `usually ~${u.secs}s here`

/** …and as read aloud, where a tilde is a word nobody meant. */
export const usualSpoken = (u: Usual): string => `usually about ${u.secs} seconds here`

/** What the figure is made of, for the line's title: the count it came from, the
 *  verb and the model, and that nothing waits on it. */
export function usualBasis(u: Usual, kind: PassTimeKind, model: ModelStamp): string {
  const verb = kind === 'executable' ? EXECUTABLE_LABEL : OP_LABEL[kind]
  return `The middle of the last ${u.passes} ${verb} passes this browser saw finish on ${model.name}. Not a promise: nothing waits on it, and a pass that runs longer is not stopped.`
}

/**
 * The pass in flight, by the feed's own rule (`feedItems`): the newest op that is
 * not a narration, while the lock is held and it has not finished. A loop from
 * the end rather than a filter, because store selectors run on every write.
 */
export function liveOpOf(session: Session, busy: boolean): Op | null {
  if (!busy) return null
  for (let i = session.ops.length - 1; i >= 0; i--) {
    const op = session.ops[i]!
    if (op.narration) continue
    return op.finishedAt === undefined ? op : null
  }
  return null
}

/**
 * Record every pass this page watched run and land — the Announcer's rule for
 * "landed" (`ui/Announcer.tsx`): an op seen unfinished and now finished. An op
 * that arrives already finished — a restored exploration, a shared copy, an undo
 * — was not run here, and adds nothing. Installed once at boot, outside React.
 * Each is filed under `passTimeKind`, read off the session it landed in: at that
 * moment an executable test's contract has not landed yet, but the press that
 * started it here has said what it is, and so has its request's receipt.
 */
export function installPassTimes(
  store: OpsStore,
  record: (op: Op, kind: PassTimeKind) => void = recordPassTime,
) {
  return watchLandings(store, (o, session) => record(o, passTimeKind(session, o)))
}
